import React from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * PermissionGate Component
 * Renders children only if user has the required permissions
 */
const PermissionGate = ({ 
  children, 
  permission = null, 
  action = null, 
  module = null, 
  roles = null,
  fallback = null,
  hideIfNoAccess = true 
}) => {
  const { 
    user, 
    hasPermission, 
    hasModulePermission, 
    canAccess 
  } = useAuth();

  // If no user is logged in, deny access
  if (!user) {
    return hideIfNoAccess ? null : fallback;
  }

  let hasAccess = true;

  // Check role-based access
  if (roles && Array.isArray(roles)) {
    hasAccess = roles.includes(user.role);
  }

  // Check module-based access
  if (module && hasAccess) {
    hasAccess = hasModulePermission ? hasModulePermission(module) : canAccess(module);
  }

  // Check permission-based access
  if (permission && hasAccess) {
    hasAccess = hasPermission(permission, action);
  }

  // Return children if user has access, otherwise return fallback or null
  return hasAccess ? children : (hideIfNoAccess ? null : fallback);
};

/**
 * ReadOnlyGate Component
 * Shows different content based on read vs edit permissions
 */
export const ReadOnlyGate = ({ 
  children, 
  readOnlyChildren, 
  permission, 
  readOnly = false 
}) => {
  const { canEditCapability } = useAuth();

  if (readOnly || !canEditCapability(permission)) {
    return readOnlyChildren || null;
  }

  return children;
};

/**
 * RoleGate Component
 * Simple role-based access control
 */
export const RoleGate = ({ children, roles, fallback = null }) => {
  const { user } = useAuth();

  if (!user || !roles.includes(user.role)) {
    return fallback;
  }

  return children;
};

/**
 * TenantGate Component
 * Controls access based on tenant-level permissions
 */
export const TenantGate = ({ 
  children, 
  allowPlatformAdmin = true, 
  allowCustomerAdmin = true,
  fallback = null 
}) => {
  const { user, isPlatformAdmin, isCustomerAdmin } = useAuth();

  if (!user) {
    return fallback;
  }

  const hasAccess = 
    (allowPlatformAdmin && isPlatformAdmin) ||
    (allowCustomerAdmin && isCustomerAdmin);

  return hasAccess ? children : fallback;
};

/**
 * AdminGate Component
 * Controls access for admin-only features
 */
export const AdminGate = ({ children, fallback = null }) => {
  const { isPlatformAdmin, isCustomerAdmin } = useAuth();
  
  return (isPlatformAdmin || isCustomerAdmin) ? children : fallback;
};

/**
 * PlatformAdminGate Component
 * Controls access for platform admin only features
 */
export const PlatformAdminGate = ({ children, fallback = null }) => {
  const { isPlatformAdmin } = useAuth();
  
  return isPlatformAdmin ? children : fallback;
};

export default PermissionGate;